// The auth screens with no form of their own, ported from their templates verbatim:
//   enable-cookies/page/enable-cookies.page.tpl.html
//   out-of-date/page/out-of-date.page.tpl.html
//   verify/page/verify.page.tpl.html
//
// Each keeps its page class, because the ported stylesheets address exactly those and the
// Phase-0 baseline photographed the result.
import type { ReactElement } from 'react'
import { AuthNav } from './AuthNav.tsx'
import { browserFamily } from '../common/browserFamily.ts'
import { GifPlayer } from '../common/GifPlayer.tsx'
import { StatusIndicationView, type StatusIndication } from './StatusIndication.tsx'
import { PageHost } from './PageHost.tsx'
import { asset } from '../assetBase.ts'

/**
 * Two `<gif-player>` widgets showing browser-specific instructions, with the browser name
 * interpolated into the gif path — the real component now (`../common/GifPlayer.tsx`),
 * SuperGif and all.
 *
 * ⚠️ THE DEFAULT IS DETECTED, NOT `'chrome'`. Three directories of GIFs exist because the
 * three browsers hide the cookie setting in three different places, and a hardcoded
 * `chrome` showed the wrong walkthrough to everyone else — see `browserFamily.ts`, where
 * that cost 4.40% of this screen's pixels for a reason that looked like layout.
 */
export function EnableCookiesPage({
  browser = browserFamily(),
  admin = false,
}: {
  browser?: string
  admin?: boolean
}): ReactElement {
  return (
    <PageHost name="chrome-extension-enable-cookies-page" className="popup-styles" hostClassName="non-extended-ui">
      {/* Upstream's logo here is deliberately NOT a link — it is the one auth screen where
          navigating away would leave the user stuck with cookies still blocked. */}
      <AuthNav admin={admin} linkTo={null} />
      <div className="page-content">
        <h1 className="enable-cookies-title">Your browser is blocking Woo from running properly</h1>
        <h2 className="enable-cookies-subtitle">To fix the problem please follow these steps</h2>
        <div className="enable-cookies-description">
          <ul className="enable-cookies-instructions">
            <li className="enable-cookies-instruction 3rd-party">
              {/* "Option 1"/"Option 2" are upstream's live titles; the descriptive ones it
                  commented out above them are deliberately not resurrected. */}
              <GifPlayer
                title="Option 1"
                gifSrc={asset(`/static/assets/img/extension/enable-cookies/${browser}/3rd-party.gif`)}
              />
            </li>
            <li className="enable-cookies-instruction specific">
              <GifPlayer
                title="Option 2"
                gifSrc={asset(`/static/assets/img/extension/enable-cookies/${browser}/domain-specific.gif`)}
              />
            </li>
          </ul>
        </div>
      </div>
    </PageHost>
  )
}

export function OutOfDatePage({ onResolve, admin = false }: { onResolve: () => void; admin?: boolean }): ReactElement {
  return (
    <PageHost name="chrome-extension-out-of-date-page" className="popup-styles" hostClassName="non-extended-ui">
      <AuthNav admin={admin} />
      <div className="page-content">
        <h1 className="title">Woo Smart Search is out-of-date</h1>
        <p className="resolve-using-popup">
          {/* Upstream opens the extension popup through the external-message channel
              (openPopup, §1.7 channel 1). Without the shell there is nothing to message, so
              the caller decides — apps/spa surfaces why rather than failing silently. */}
          <a
            className="cursor-pointer"
            href=""
            onClick={(event) => {
              event.preventDefault()
              onResolve()
            }}
          >
            Click here to update
          </a>
        </p>
      </div>
    </PageHost>
  )
}

/**
 * `verify.page.tpl.html` is four lines: a nav and
 * `<status-indication status="ctrl.status" loading="ctrl.loading">`. So this takes the
 * indication rather than a made-up status union — the first port invented `'loading' |
 * 'success' | 'failure'` and its own copy strings, which matched nothing upstream renders.
 *
 * ⚠️ It uses the SHARED `StatusIndicationView`, not an inline copy. The inline one was the
 * whole bug: `_verify.page.scss:33` scopes this screen's 32px spacing under the
 * `<status-indication>` ELEMENT, and a bare `.status-indication` div matches the class rules
 * while missing every one of those. `StatusPage` was fixed for exactly this and verify was
 * missed — which is what a duplicate buys you.
 */
export function VerifyPage({
  status,
  loading = false,
  admin = false,
}: {
  status: StatusIndication
  loading?: boolean
  admin?: boolean
}): ReactElement {
  return (
    <PageHost name="chrome-extension-verify-page" className="popup-styles" hostClassName="non-extended-ui">
      <AuthNav admin={admin} linkTo={null} />
      <div className="page-content">
        <StatusIndicationView status={status} loading={loading} />
      </div>
    </PageHost>
  )
}
